"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FetchAPI = void 0;
const tslib_1 = require("tslib");
const isArray_1 = tslib_1.__importDefault(require("lodash/isArray"));
const isObject_1 = tslib_1.__importDefault(require("lodash/isObject"));
const common_1 = require("./common");
const query_string_1 = require("./query-string");
const objects_1 = require("./objects");
class FetchAPI {
    constructor(config, init) {
        var _a, _b, _c;
        this.init = {
            mode: 'cors',
            cache: 'default',
        };
        this.listeners = [];
        this.config = {
            baseURL: '',
            request: {
                convertCase: null,
                beforeEach: (config) => config,
            },
            response: {
                convertCase: null,
            },
        };
        this.next = (request, onSuccess, onError) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const [err, res] = yield request;
            if (!err && onSuccess)
                yield onSuccess(res);
            if (err && onError)
                yield onError(err);
            return [err, res];
        });
        /**
         *
         * @deprecated use next instead
         */
        this.toCallback = (request, callback) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const res = yield request;
            if (callback)
                callback(res);
            return res;
        });
        this.baseURL = config.baseURL || '';
        // CONFIG
        this.config = {
            baseURL: config.baseURL || this.config.baseURL,
            request: {
                convertCase: ((_a = config.request) === null || _a === void 0 ? void 0 : _a.convertCase) || this.config.request.convertCase,
                beforeEach: ((_b = config.request) === null || _b === void 0 ? void 0 : _b.beforeEach) || this.config.request.beforeEach,
            },
            response: {
                convertCase: ((_c = config.response) === null || _c === void 0 ? void 0 : _c.convertCase) || this.config.response.convertCase,
            },
        };
        if (init)
            this.init = Object.assign(Object.assign({}, this.init), init);
        this.headers = { Accept: 'application/json' };
    }
    ensureCase(data, caseType) {
        if (caseType === 'camelCase')
            return (0, objects_1.convertKeysToCamelCase)(data);
        if (caseType === 'snake_case')
            return (0, objects_1.convertKeysToSnakeCase)(data);
        return data;
    }
    getUrl(endpoint, query) {
        // If there is no query
        if (!query)
            return `${this.baseURL}${endpoint}`;
        // converting query to snake case if api is in snake case
        query = this.ensureCase(query, this.config.request.convertCase);
        // returning formatted url
        return `${this.baseURL}${endpoint}?${query_string_1.queryString.stringify(query)}`;
    }
    handleFetch(promise, meta) {
        let response = {
            status: 'net',
            ref: () => { },
            statusText: 'ERR_CONNECTION_REFUSED',
        };
        return new Promise((resolve) => {
            let isResolved = false;
            const onResolve = (data) => {
                // Aleady resolved
                if (isResolved)
                    return;
                // Resolve with the response
                resolve(data);
                // Calling the listeners
                this.listeners.forEach((listener) => listener(data, meta));
                // Setting resolved to true
                isResolved = true;
            };
            return promise
                .then((raw) => {
                // Saving the status
                response.status = raw.status;
                response.statusText = raw.statusText;
                response.ref = () => raw;
                // Sending json response
                if (raw.ok)
                    return raw.json();
                // Parsing error text
                return raw.text().then((text) => {
                    // Parsing error text
                    let [err, errParsed] = (0, common_1.jsonParse)(text);
                    // Still text
                    if (!err && !(0, isObject_1.default)(errParsed)) {
                        response.message = errParsed;
                        return onResolve([true, response]);
                    }
                    // Error body is plain text
                    if (err)
                        throw new Error(text);
                    // Erorr body is object
                    response = Object.assign(Object.assign({}, response), errParsed);
                    return onResolve([true, response]);
                });
            })
                .then((json) => {
                if (!(0, isObject_1.default)(json)) {
                    response.data = json;
                    return onResolve([false, response]);
                }
                // Converting to camelCase if api is in snake_case
                json = this.ensureCase(json, this.config.response.convertCase);
                if ((0, isArray_1.default)(json))
                    response.data = json;
                //
                else {
                    response = Object.assign(Object.assign({}, response), json);
                }
                // Sending success response
                return onResolve([false, response]);
            })
                .catch((err) => {
                // Sending error response
                response.ref = () => err;
                response.message = err.message;
                return onResolve([true, response]);
            });
        });
    }
    parseHeaders(headers) {
        const parsed = {};
        headers.forEach((value, key) => {
            parsed[key] = value;
        });
        return parsed;
    }
    getHeaders() {
        const headers = new Headers();
        Object.entries(this.headers).forEach(([key, value]) => {
            headers.append(key, value);
        });
        return headers;
    }
    setHeaders(headers) {
        this.headers = Object.assign(Object.assign({}, this.headers), headers);
    }
    subscribe(listener) {
        if (!this.listeners.includes(listener)) {
            this.listeners.push(listener);
        }
        return () => this.unsubscribe(listener);
    }
    unsubscribe(listener) {
        const index = this.listeners.indexOf(listener);
        if (index !== -1)
            this.listeners.splice(index, 1);
    }
    removeHeaders(...keys) {
        keys.forEach((key) => {
            delete this.headers[key];
        });
    }
    get(endpoint, query) {
        const input = this.getUrl(endpoint, query);
        const headers = this.getHeaders();
        return this.handleFetch(fetch(input, this.config.request.beforeEach({
            headers,
            method: 'GET',
        })), { input });
    }
    post(endpoint, body, query) {
        const input = this.getUrl(endpoint, query);
        const headers = this.getHeaders();
        if (!(body instanceof FormData)) {
            body = this.ensureCase(body, this.config.request.convertCase);
            body = JSON.stringify(body);
            headers.append('Content-Type', 'application/json');
        }
        return this.handleFetch(fetch(input, this.config.request.beforeEach({
            body,
            headers,
            method: 'POST',
        })), { input });
    }
    patch(endpoint, body, query) {
        const input = this.getUrl(endpoint, query);
        const headers = this.getHeaders();
        if (!(body instanceof FormData)) {
            body = this.ensureCase(body, this.config.request.convertCase);
            body = JSON.stringify(body);
            headers.append('Content-Type', 'application/json');
        }
        return this.handleFetch(fetch(input, this.config.request.beforeEach({
            body,
            headers,
            method: 'PATCH',
        })), { input });
    }
    put(endpoint, body, query) {
        const input = this.getUrl(endpoint, query);
        const headers = this.getHeaders();
        if (!(body instanceof FormData)) {
            body = this.ensureCase(body, this.config.request.convertCase);
            body = JSON.stringify(body);
            headers.append('Content-Type', 'application/json');
        }
        return this.handleFetch(fetch(input, this.config.request.beforeEach({
            body,
            headers,
            method: 'PUT',
        })), { input });
    }
    delete(endpoint) {
        const input = this.getUrl(endpoint);
        const headers = this.getHeaders();
        return this.handleFetch(fetch(input, this.config.request.beforeEach({
            headers,
            method: 'DELETE',
        })), { input });
    }
}
exports.FetchAPI = FetchAPI;
